/**
 * Edit agreement / Start date for one linked bike.
 *
 * The rider's assignment start and the investor's ownership start are kept
 * apart (with a switch to set both to the same day), with the weekly share.
 * Nothing is saved until the preview has been seen: it shows every affected
 * week and payment, the investor's figures before and after, and what moves
 * out of company revenue. "Save and recalculate" applies exactly that, at
 * once.
 */

import { useState, type ReactNode } from 'react';
import type { InvestorBikeLink, LinkCorrectionResult } from '@cenox/shared';
import { Button, ErrorState, Input, Modal, cn } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { usePreviewLinkCorrection, useSaveLinkCorrection } from './investorData';

const CHANGE_TEXT: Record<LinkCorrectionResult['weeks'][number]['change'], string> = {
  attached: 'Attached to this bike',
  charged: 'Charged now',
  redivided: 'Re-divided',
  unchanged: 'Unchanged',
};

export function CorrectLinkDialog({
  investorId,
  investorName,
  link,
  onClose,
  onDone,
}: {
  investorId: string;
  investorName: string;
  link: InvestorBikeLink;
  onClose: () => void;
  onDone: (message: string) => void;
}): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const preview = usePreviewLinkCorrection();
  const save = useSaveLinkCorrection();
  const ownershipStart = link.effectiveFrom <= '2000-01-01' ? '' : link.effectiveFrom;
  const [riderStartedOn, setRiderStartedOn] = useState(link.riderSince ?? '');
  const [ownershipFrom, setOwnershipFrom] = useState(ownershipStart);
  const [same, setSame] = useState(false);
  const [weeklyAmount, setWeeklyAmount] = useState(link.weeklyShare);
  const [chargeUnchargedWeeks, setChargeUnchargedWeeks] = useState(false);
  const [reason, setReason] = useState('');
  const [key, setKey] = useState(() => `link-correction-${crypto.randomUUID()}`);
  const error = save.error ?? preview.error;
  const fields = error instanceof ApiError ? error.fields : undefined;
  const result = preview.data?.data;

  const input = (recalculateOnly: boolean) => ({
    reason: reason.trim(),
    ...(recalculateOnly
      ? {}
      : {
          ...(link.riderId && riderStartedOn && riderStartedOn !== link.riderSince ? { riderStartedOn } : {}),
          ...((same ? riderStartedOn : ownershipFrom) && (same ? riderStartedOn : ownershipFrom) !== link.effectiveFrom
            ? { ownershipFrom: same ? riderStartedOn : ownershipFrom }
            : {}),
          ...(weeklyAmount && weeklyAmount !== link.weeklyShare ? { weeklyAmount } : {}),
        }),
    ...(chargeUnchargedWeeks ? { chargeUnchargedWeeks: true } : {}),
  });
  const [last, setLast] = useState<ReturnType<typeof input> | null>(null);

  const run = (recalculateOnly: boolean) => {
    const body = input(recalculateOnly);
    setLast(body);
    setKey(`link-correction-${crypto.randomUUID()}`);
    save.reset();
    preview.mutate({ investorId, bikeId: link.bikeId, input: body });
  };

  return (
    <Modal open onClose={onClose} title={`Edit agreement / Start date — ${link.registration}`} size="lg">
      <div className="space-y-4">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 rounded-lg border border-slate-200 p-3 text-sm sm:grid-cols-2" aria-label="Link">
          <div className="flex gap-2"><dt className="w-28 shrink-0 text-slate-500">Bike</dt><dd className="font-medium">{link.registration}</dd></div>
          <div className="flex gap-2"><dt className="w-28 shrink-0 text-slate-500">Investor</dt><dd>{investorName}</dd></div>
          <div className="flex gap-2"><dt className="w-28 shrink-0 text-slate-500">Rider</dt><dd>{link.riderName ?? 'None assigned'}</dd></div>
          <div className="flex gap-2"><dt className="w-28 shrink-0 text-slate-500">Weekly share</dt><dd>{link.sharePercent ? `${link.sharePercent}% of each week’s remittance` : money(link.weeklyShare, currency)}</dd></div>
        </dl>

        <form
          aria-label="Correct link dates"
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            run(false);
          }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label="Rider assignment start"
              type="date"
              max={today}
              value={riderStartedOn}
              disabled={!link.riderId}
              onChange={(e) => setRiderStartedOn(e.target.value)}
              hint={link.riderId ? `Now ${link.riderSince ?? '—'}` : 'No rider assigned'}
              error={fields?.riderStartedOn?.[0]}
            />
            <Input
              label="Investor agreement start"
              type="date"
              max={today}
              value={same ? riderStartedOn : ownershipFrom}
              disabled={same}
              onChange={(e) => setOwnershipFrom(e.target.value)}
              hint={`Now ${ownershipStart || 'since registration'}`}
              error={fields?.ownershipFrom?.[0]}
            />
            {link.sharePercent ? (
              <p className="self-end text-xs text-slate-500">
                A percentage share: change it from the vehicle partnership (Change vehicle terms).
              </p>
            ) : (
              <Input
                label={`Weekly investor share (${currency})`}
                inputMode="decimal"
                value={weeklyAmount}
                onChange={(e) => setWeeklyAmount(e.target.value)}
                error={fields?.weeklyAmount?.[0]}
              />
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="size-4" checked={same} disabled={!link.riderId} onChange={(e) => setSame(e.target.checked)} />
            Use the same date for both
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="size-4" checked={chargeUnchargedWeeks} onChange={(e) => setChargeUnchargedWeeks(e.target.checked)} />
            Also charge the rider’s ended weeks on this bike that were never charged
          </label>
          <Input label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Rider started on this bike earlier" error={fields?.reason?.[0]} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => run(true)} disabled={!reason.trim() || preview.isPending}>
              Recalculate from start date
            </Button>
            <Button type="submit" variant="secondary" loading={preview.isPending} disabled={!reason.trim() || preview.isPending}>
              Preview changes
            </Button>
          </div>
        </form>

        {error && (!fields || Object.keys(fields).length === 0) ? <ErrorState message={error.message} /> : null}
        {error && fields && Object.keys(fields).length > 0 ? (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </p>
        ) : null}

        {result ? (
          <PreviewPanel result={result} currency={currency} />
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-3">
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={!result || !result.changed || !last || save.isPending}
            onClick={() =>
              save.mutate(
                { investorId, bikeId: link.bikeId, input: { ...last!, idempotencyKey: key } },
                {
                  onSuccess: (r) =>
                    onDone(
                      r.data.changed
                        ? `${link.registration} recalculated from ${r.data.recalculatedFrom}: ${r.data.correctionsRecorded} payment correction(s), ` +
                            `company revenue ${money(r.data.companyRevenueAdjustment, currency)}. ${investorName} is owed ${money(r.data.figuresAfter.owed, currency)}.`
                        : 'Nothing needed changing.',
                    ),
                },
              )
            }
          >
            Save and recalculate
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PreviewPanel({ result, currency }: { result: LinkCorrectionResult; currency: string }): ReactNode {
  const fig = (k: 'entitled' | 'collected' | 'unpaidByRiders' | 'paidOut' | 'owed') => [result.figuresBefore[k], result.figuresAfter[k]] as const;
  return (
    <section className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3" aria-label="Preview" data-testid="correction-preview">
      <p className="text-sm text-slate-800">
        {result.changed ? (
          <>
            Recalculated from <strong>{result.recalculatedFrom}</strong>. Agreement start {result.before.ownershipFrom} →{' '}
            {result.after.ownershipFrom}; rider start {result.before.riderStartedOn ?? '—'} → {result.after.riderStartedOn ?? '—'};
            share {result.before.sharePercent ? `${result.before.sharePercent}%` : money(result.before.weeklyShare, currency)} →{' '}
            {result.after.sharePercent ? `${result.after.sharePercent}%` : money(result.after.weeklyShare, currency)}.
          </>
        ) : (
          'Everything already matches these dates. Nothing would change.'
        )}
      </p>
      <p className="text-xs text-slate-500">
        No receipt, payment or payout is created. Payments keep their dates and amounts; where their investor and company
        shares change, a correcting entry is recorded. Unpaid weeks are never counted as collected.
      </p>

      <div className="-mx-1 overflow-x-auto px-1">
        <table className="w-full min-w-[420px] text-sm" aria-label="Investor figures">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-1 pr-3 font-medium" />
              <th className="py-1 pr-3 text-right font-medium">Before</th>
              <th className="py-1 text-right font-medium">After</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {(
              [
                ['Entitlement accrued', 'entitled'],
                ['Collected from rider payments', 'collected'],
                ['Still unpaid by riders', 'unpaidByRiders'],
                ['Paid out to the investor', 'paidOut'],
                ['Collected, still owed to the investor', 'owed'],
              ] as const
            ).map(([label, k]) => {
              const [b, a] = fig(k);
              return (
                <tr key={k}>
                  <td className="py-1.5 pr-3 text-slate-700">{label}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-slate-500">{money(b, currency)}</td>
                  <td className={cn('py-1.5 text-right font-medium tabular-nums', Number(a) < 0 ? 'text-red-700' : 'text-slate-900')}>{money(a, currency)}</td>
                </tr>
              );
            })}
            <tr>
              <td className="py-1.5 pr-3 text-slate-700">Company remittance revenue</td>
              <td className="py-1.5 pr-3" />
              <td className="py-1.5 text-right font-medium tabular-nums" data-testid="revenue-adjustment">
                {Number(result.companyRevenueAdjustment) > 0 ? '+' : ''}
                {money(result.companyRevenueAdjustment, currency)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {result.flags.length > 0 ? (
        <ul className="space-y-1 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" aria-label="Needs review">
          {result.flags.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      ) : null}

      {result.weeks.length > 0 ? (
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full min-w-[820px] text-xs" aria-label="Affected weeks">
            <thead>
              <tr className="text-left uppercase tracking-wide text-slate-500">
                <th className="py-1 pr-2 font-medium">Week</th>
                <th className="py-1 pr-2 font-medium">Rider</th>
                <th className="py-1 pr-2 font-medium">Change</th>
                <th className="py-1 pr-2 text-right font-medium">Due</th>
                <th className="py-1 pr-2 text-right font-medium">Paid</th>
                <th className="py-1 pr-2 text-right font-medium">Investor entitled</th>
                <th className="py-1 pr-2 text-right font-medium">Investor collected</th>
                <th className="py-1 text-right font-medium">Company collected</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {result.weeks.map((w) => (
                <tr key={w.remittanceId} className="align-top">
                  <td className="py-1 pr-2 whitespace-nowrap">{w.weekStartsOn} – {w.weekEndsOn}</td>
                  <td className="py-1 pr-2">
                    {w.riderName}
                    {w.heldDays !== null && w.sharedDays ? <span className="block text-slate-400">held {w.heldDays} of {w.sharedDays} days</span> : null}
                    {w.review ? <span className="block max-w-xs text-amber-700">{w.review}</span> : null}
                  </td>
                  <td className="py-1 pr-2">{CHANGE_TEXT[w.change]}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{money(w.amountDue, currency)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{money(w.paid, currency)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{money(w.investorEntitlementBefore, currency)} → {money(w.investorEntitlementAfter, currency)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{money(w.investorCollectedBefore, currency)} → {money(w.investorCollectedAfter, currency)}</td>
                  <td className="py-1 text-right tabular-nums">{money(w.companyCollectedBefore, currency)} → {money(w.companyCollectedAfter, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {result.payments.length > 0 ? (
        <div className="-mx-1 overflow-x-auto px-1">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Payments already recorded</p>
          <table className="w-full min-w-[620px] text-xs" aria-label="Existing payments">
            <thead>
              <tr className="text-left uppercase tracking-wide text-slate-500">
                <th className="py-1 pr-2 font-medium">Paid on</th>
                <th className="py-1 pr-2 font-medium">Rider · week</th>
                <th className="py-1 pr-2 text-right font-medium">Amount</th>
                <th className="py-1 pr-2 text-right font-medium">Investor</th>
                <th className="py-1 text-right font-medium">Company</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {result.payments.map((p) => (
                <tr key={p.paymentId}>
                  <td className="py-1 pr-2">{p.paidOn}</td>
                  <td className="py-1 pr-2">{p.riderName} · {p.weekStartsOn}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{money(p.amount, currency)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{money(p.investorBefore, currency)} → {money(p.investorAfter, currency)}</td>
                  <td className="py-1 text-right tabular-nums">{money(p.companyBefore, currency)} → {money(p.companyAfter, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {result.unchargedWeeks.length > 0 ? (
        <div className="text-xs text-slate-700" aria-label="Uncharged weeks">
          <p className="font-medium">Weeks on this bike that were never charged</p>
          <ul className="mt-1 list-disc pl-5">
            {result.unchargedWeeks.map((w) => (
              <li key={w.weekStartsOn}>
                {w.weekStartsOn} – {w.weekEndsOn}: {money(w.amount, currency)} {w.charged ? '— will be charged' : '— not charged (tick the box above to charge)'}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
